import { Button, Col, Input, Layout, Row, Checkbox, InputNumber } from "antd";
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { useTasks } from "src/context/tasks";
import { useResizeObserver } from "src/hooks/useResizeObserver";
import { logEvent } from "src/analytics/amplitude";
import { openNotificationWithIcon } from "src/components/utils/OpenNotificationWithIcon";
import MobileNavbar from "src/components/utils/MobileNavbar";
import { useMutation, useClient } from "urql";
import { VerifyAddress } from "src/graphql/mutations/address";
import { addressRefusal, withZip } from "src/lib/address";
import {
  validateEmail,
  validatePhone,
  formatToPhone,
  debounce,
} from "src/weirdStuff";
import { contactOptions } from "./StepFour";
import { StyledRadioGroup } from "src/components/utils/RadioGroup";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElement } from "@stripe/stripe-js";
import {
  createPaymentIntent,
  updatePaymentIntent,
  verifyPayment,
} from "src/weirdStuff/api";
import { useConfirmation } from "src/context/confirmation";
import { useNavigate } from "react-router-dom";
import { GET_USER_CREDITS } from "src/graphql/queries/user";
import { CreditLoginModal } from "src/components/utils/CreditLoginModal";
import { useUser } from "src/context/user";

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 32px;
  margin-bottom: 24px;

  @media screen and (max-width: 500px) {
    display: none;
  }
`;

const StyledButton = styled(Button)`
  min-height: 44px;
  padding: 0 24px;
  border-radius: 6px;
  font-weight: 500;
`;

const PreviousButton = styled(StyledButton)`
  border: 1px solid #e2e2e2;
  color: #666;

  &:hover {
    border-color: #d3d3d3;
    color: #373744;
  }
`;

const NextButton = styled(StyledButton)`
  background-color: var(--blue-300);
  color: white;
  border: none;

  &:hover {
    background-color: var(--blue-400);
  }

  &:disabled {
    background-color: #e2e2e2;
    color: #999;
  }
`;

interface TaskContactInfoProps {
  changeTaskPage: (page: number) => void;
}

interface TaskContactInfoState {
  fullname: string;
  email: string;
  phone: string;
  address: string;
  apartmentNo: string;
}

interface TaskContactInfoAction {
  type:
    | "setFullname"
    | "setEmail"
    | "setPhone"
    | "setAddress"
    | "setApartmentNo";
  payload: string;
}

function contactInfoReducer(
  state: TaskContactInfoState,
  action: TaskContactInfoAction
) {
  switch (action.type) {
    case "setFullname":
      return { ...state, fullname: action.payload };
    case "setEmail":
      return { ...state, email: action.payload };
    case "setPhone":
      return { ...state, phone: action.payload };
    case "setAddress":
      return { ...state, address: action.payload };
    case "setApartmentNo":
      return { ...state, apartmentNo: action.payload };
  }
}

export default function TaskContactInfo({
  changeTaskPage,
}: TaskContactInfoProps) {
  const {
    contactInfo,
    setContactInfo,
    selectedTasks,
    scheduleInfo,
    paymentIntent: cachedPaymentIntent,
    setPaymentIntent,
    tipAmount,
    setTipAmount,
  } = useTasks();
  const width = useResizeObserver();
  const isMobile = width <= 500;
  const [loading, setLoading] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  const [state, dispatch] = useReducer(contactInfoReducer, contactInfo);

  const { confirmBooking } = useConfirmation();

  const navigate = useNavigate();

  const client = useClient();
  const userContext = useUser();

  // Credit modal state
  const [showCreditModal, setShowCreditModal] = useState(false);
  const userCreditsRef = useRef<{
    email: string;
    credits: number;
    authType?: "email" | "google-oauth2" | null;
  }>({ email: "", credits: 0, authType: null });

  // Validation errors
  const [errors, setErrors] = useState({
    fullname: "",
    email: "",
    phone: "",
    address: "",
  });

  // Payment states
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [tipOption, setTipOption] = useState<string>("");
  const [customTipAmount, setCustomTipAmount] = useState(0);

  const [, verifyAddress] = useMutation(VerifyAddress);

  const tipOptions = [
    { label: "$5", value: "5" },
    { label: "$10", value: "10" },
    { label: "$15", value: "15" },
    { label: "Other", value: "other" },
  ];

  // Check if a user account exists with credits
  const checkEmail = async (email: string) => {
    console.log("Checking email for credits...", email);

    const { data, error } = await client
      .query(GET_USER_CREDITS, {
        email,
      })
      .toPromise();

    if (error) {
      console.error(error);
      return;
    }

    if (data.user.credits > 0) {
      userCreditsRef.current = {
        email,
        credits: data.user.credits,
        authType: data.user.auth_type,
      };
      setShowCreditModal(true);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkForCredits = useCallback(
    debounce((email: string) => checkEmail(email), 3000),
    []
  );

  // Check for credits when email changes
  useEffect(() => {
    if (state.email && !errors.email && validateEmail(state.email)) {
      checkForCredits(state.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.email]);

  const handleCreditLoginSuccess = (
    token: string,
    credits: number,
    authType?: string | null
  ) => {
    userContext.setCredentials(token, credits, authType);
    setShowCreditModal(false);
  };

  // Calculate pricing
  const subtotal = Math.round(
    selectedTasks.reduce(
      (sum, task) => sum + (task.current_price_cents || 0),
      0
    ) / 100
  );
  const tax = Math.round((subtotal * 8.875) / 100);
  const total = subtotal + tax;

  const handleTipOptionChange = (value: string) => {
    setTipOption(value);
    if (value === "other") {
      setTipAmount(customTipAmount);
    } else {
      setTipAmount(Number(value));
    }
  };

  const handleCustomTipChange = (value: number | null) => {
    const amount = Math.min(Math.max(value || 0, 0), 100);
    setCustomTipAmount(amount);
    if (tipOption === "other") {
      setTipAmount(amount);
    }
  };

  const handlePrevious = () => {
    // Save current state before navigating back
    dispatch({ type: "setFullname", payload: state.fullname.trim() });
    dispatch({ type: "setEmail", payload: state.email.trim() });
    dispatch({ type: "setPhone", payload: state.phone.trim() });
    dispatch({ type: "setAddress", payload: state.address.trim() });
    dispatch({ type: "setApartmentNo", payload: state.apartmentNo.trim() });
    changeTaskPage(2);
  };

  const validateForm = (): boolean => {
    const newErrors = {
      fullname: "",
      email: "",
      phone: "",
      address: "",
    };

    let isValid = true;

    if (!state.fullname.trim()) {
      newErrors.fullname = "Please enter your full name";
      isValid = false;
    } else if (state.fullname.trim().length <= 1) {
      newErrors.fullname = "Well, that's a short name :/";
      isValid = false;
    } else if (state.fullname.trim().length > 48) {
      newErrors.fullname =
        "Woah, that's a long name. We only support 48 characters.";
      isValid = false;
    }

    if (!state.email.trim()) {
      newErrors.email = "Please enter your email";
      isValid = false;
    } else if (!validateEmail(state.email)) {
      newErrors.email = "Please enter a valid email";
      isValid = false;
    }

    if (!state.phone.trim()) {
      newErrors.phone = "Please enter your phone number";
      isValid = false;
    } else if (!validatePhone(state.phone)) {
      newErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    if (!state.address.trim()) {
      newErrors.address = "Please enter your service address";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNext = async () => {
    // Validate form
    if (!validateForm()) {
      return;
    }

    if (!termsAccepted) {
      openNotificationWithIcon(
        "error",
        "Terms not accepted",
        "Please accept the terms and conditions"
      );
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    // Verify address first
    const addressResult = await verifyAddress({
      address: state.address.trim(),
    });

    const verified = addressResult.data?.verifyAddress;
    const refusal = addressRefusal(verified);
    if (refusal) {
      setLoading(false);
      openNotificationWithIcon("error", refusal.title, refusal.description);
      return;
    }

    // Save contact info with verified address
    const verifiedAddress = withZip(state.address, verified.zip);

    setContactInfo({
      fullname: state.fullname.trim(),
      email: state.email.trim(),
      phone: state.phone.trim(),
      contactType: "",
      address: verifiedAddress,
      apartmentNo: state.apartmentNo.trim(),
    });

    try {
      // Check if user has enough credits to cover the total
      const totalWithTip = total + tipAmount;
      const canUseCredits = userContext.credits >= totalWithTip;

      let paymentMethodId = "";

      // If credits don't cover the total, we need to create a payment method
      if (!canUseCredits) {
        const cardElement = elements.getElement(
          CardElement
        ) as StripeCardElement;

        const pm = await stripe.createPaymentMethod({
          billing_details: {
            name: state.fullname,
            email: state.email,
            phone: state.phone,
          },
          type: "card",
          card: cardElement,
        });

        if (pm?.error) {
          setLoading(false);
          return openNotificationWithIcon(
            "error",
            "Error",
            pm.error?.message ?? "Failed to validate card details"
          );
        }

        paymentMethodId = pm.paymentMethod.id;
      }

      const payload = {
        fullname: state.fullname,
        email: state.email,
        phone: state.phone,
        address: verifiedAddress,
        apartmentNo: state.apartmentNo,
        contactType: contactInfo.contactType,
        paymentMethodId,
        total,
        tip: tipAmount || 0,
        scheduled_date: scheduleInfo.date,
        scheduled_time: scheduleInfo.time,
        tasks: selectedTasks.map((task) => ({
          service_id: task.service_id,
          title: task.title,
          price_cents: task.current_price_cents,
          category_id: task.category.category_id,
        })),
        instructions: scheduleInfo.instructions || "",
      };

      logEvent("User completed task payment", {
        tasksCount: selectedTasks.length,
        total: totalWithTip,
        hasCoupon: false,
        hasTip: tipAmount > 0,
        usedCredits: canUseCredits,
      });

      // if we have a previously saved payment_intent, we might want to verify if it's upto date,
      // if not, we'll create a new payment_intent
      const paymentIntent = cachedPaymentIntent
        ? await updatePaymentIntent(
            cachedPaymentIntent,
            payload,
            userContext.token,
            "tasks"
          ).catch(() => setLoading(false))
        : await createPaymentIntent(payload, userContext.token, "tasks").catch(
            () => setLoading(false)
          );

      if (!paymentIntent || "error" in paymentIntent) {
        console.error(paymentIntent?.error);
        logEvent("Payment setup failed", {
          user: state.fullname,
          source: "tasks",
          error: paymentIntent?.error as string,
        });
        openNotificationWithIcon(
          "error",
          "Failed to complete booking",
          paymentIntent?.error as string
        );
        setLoading(false);
        return;
      }

      setPaymentIntent(paymentIntent);

      // If user has credits that cover the total, skip card payment
      if (canUseCredits) {
        const result = await verifyPayment({
          payment_intent: paymentIntent.id,
          payment_type: "cash",
          tasks: payload,
          token: userContext.token,
          source: "tasks",
        });

        if ("error" in result) {
          setLoading(false);
          openNotificationWithIcon(
            "error",
            "Payment failed",
            (result.error as string) || ""
          );
          return;
        }

        if (result.data?.total_amount) {
          confirmBooking({
            email: result.data.email,
            fullname: result.data.fullname,
            clean_date: result.data.scheduled_date,
            total_amount: result.data.total_amount,
            bookingId: result.data.bookingId || "",
            source: "tasks",
          });
          navigate("/confirmation", { replace: true });
          return;
        }

        setLoading(false);
        return openNotificationWithIcon(
          "error",
          "Payment failed",
          "Something went wrong"
        );
      }

      // For card payment, if payment status is 'requires_action' or
      // 'requires_payment_method', we'll need to confirm the card payment.
      if (
        paymentIntent.status === "requires_action" ||
        paymentIntent.status === "requires_payment_method" ||
        paymentIntent.status === "requires_confirmation"
      ) {
        const result = await stripe?.confirmCardPayment(
          paymentIntent.client_secret!,
          {
            payment_method: paymentMethodId,
          }
        );
        if (result?.error) {
          setLoading(false);
          openNotificationWithIcon(
            "error",
            "Payment failed",
            result?.error?.message ?? "Something went wrong"
          );
          return;
        }
      }

      const result = await verifyPayment({
        payment_intent: paymentIntent.id,
        payment_type: "stripe",
        tasks: payload,
        token: userContext.token,
        source: "tasks",
      });

      if ("error" in result) {
        setLoading(false);
        openNotificationWithIcon(
          "error",
          "Payment failed",
          (result.error as string) || ""
        );
        return;
      }

      if (result.data?.total_amount) {
        confirmBooking({
          email: result.data.email,
          fullname: result.data.fullname,
          clean_date: result.data.scheduled_date,
          total_amount: result.data.total_amount,
          bookingId: result.data.bookingId || "",
          source: "tasks",
        });
        navigate("/confirmation", { replace: true });
        return;
      }

      setLoading(false);
      openNotificationWithIcon(
        "error",
        "Payment failed",
        "Something went wrong"
      );
    } catch (error) {
      setLoading(false);
      console.error("Payment error:", error);
      openNotificationWithIcon(
        "error",
        "Payment failed",
        "An error occurred while processing your payment"
      );
    }
  };

  return (
    <Layout className="step-layout mobile-step-layout">
      <h1 className="semi-bold">Alright, Let's get this wrapped up.</h1>
      <p>Now just enter your contact, service address and payment details.</p>
      <h2 className="semi-bold" style={{ fontSize: "1.2rem" }}>
        Your details
      </h2>

      <Row gutter={[16, 16]}>
        <Col lg={12} xs={24}>
          <label htmlFor="fullname" className="mt-2">
            Full Name
          </label>
          <br />
          <Input
            className="input"
            size="large"
            id="fullname"
            name="fullname"
            value={state.fullname}
            onChange={(e) => {
              dispatch({ type: "setFullname", payload: e.target.value });
              if (errors.fullname) {
                setErrors({ ...errors, fullname: "" });
              }
            }}
            onBlur={() => {
              if (state.fullname.trim() && state.fullname.trim().length > 48) {
                setErrors({
                  ...errors,
                  fullname:
                    "Woah, that's a long name. We only support 48 characters.",
                });
              }
            }}
            placeholder="John Doe"
            style={{
              fontSize: isMobile ? "16px" : "14px",
              borderColor: errors.fullname ? "red" : "#d9d9d9",
            }}
          />
          {errors.fullname && (
            <small style={{ color: "red", display: "block", marginBottom: 5 }}>
              {errors.fullname}
            </small>
          )}
        </Col>

        <Col lg={12} xs={24}>
          <label htmlFor="email" className="mt-2">
            Email Address
          </label>
          <br />
          <Input
            className="input"
            size="large"
            type="email"
            id="email"
            name="email"
            value={state.email}
            onChange={(e) => {
              dispatch({ type: "setEmail", payload: e.target.value });
              if (errors.email) {
                setErrors({ ...errors, email: "" });
              }
            }}
            onBlur={() => {
              if (state.email.trim() && !validateEmail(state.email)) {
                setErrors({ ...errors, email: "Please enter a valid email" });
              }
            }}
            placeholder="john@example.com"
            style={{
              fontSize: isMobile ? "16px" : "14px",
              borderColor: errors.email ? "red" : "#d9d9d9",
            }}
          />
          {errors.email && (
            <small style={{ color: "red", display: "block", marginBottom: 5 }}>
              {errors.email}
            </small>
          )}
        </Col>

        <Col lg={12} xs={24}>
          <label htmlFor="phone" className="mt-2">
            Phone Number
          </label>
          <br />
          <Input
            id="phone"
            name="phone"
            className="input"
            size="large"
            type="tel"
            prefix="+1"
            autoComplete="tel"
            value={state.phone}
            onChange={(e) => {
              formatToPhone(e);
              dispatch({ type: "setPhone", payload: e.target.value });
              if (errors.phone) {
                setErrors({ ...errors, phone: "" });
              }
            }}
            onBlur={() => {
              if (state.phone.trim() && !validatePhone(state.phone)) {
                setErrors({
                  ...errors,
                  phone: "Please enter a valid phone number",
                });
              }
            }}
            placeholder="(555) 123 - 4567"
            style={{
              fontSize: isMobile ? "16px" : "14px",
              borderColor: errors.phone ? "red" : "#d9d9d9",
            }}
          />
          {errors.phone && (
            <small style={{ color: "red", display: "block", marginBottom: 5 }}>
              {errors.phone}
            </small>
          )}
        </Col>
        <Col lg={12} xs={24}>
          <StyledRadioGroup
            id="contactType"
            label="Contact Type"
            size="small"
            options={contactOptions}
          />
        </Col>
      </Row>

      <br />
      <br />

      <h2 className="semi-bold" style={{ fontSize: "1.2rem" }}>
        Service Address
      </h2>

      <Row gutter={[16, 16]}>
        <Col lg={12} xs={24}>
          <label htmlFor="address" className="mt-2">
            Address
          </label>
          <br />
          <Input
            className="input"
            size="large"
            id="address"
            name="address"
            value={state.address}
            onChange={(e) => {
              dispatch({ type: "setAddress", payload: e.target.value });
              if (errors.address) {
                setErrors({ ...errors, address: "" });
              }
            }}
            placeholder="123 Madison St, New York"
            style={{
              fontSize: isMobile ? "16px" : "14px",
              borderColor: errors.address ? "red" : "#d9d9d9",
            }}
          />
          {errors.address && (
            <small style={{ color: "red", display: "block", marginBottom: 5 }}>
              {errors.address}
            </small>
          )}
        </Col>

        <Col lg={12} xs={24}>
          <span className="mt-2">
            Apartment No. <small className="optional">(Optional)</small>
          </span>
          <br />
          <Input
            className="input"
            size="large"
            value={state.apartmentNo.trim()}
            onChange={(e) =>
              dispatch({
                type: "setApartmentNo",
                payload: e.target.value.trim(),
              })
            }
            placeholder="1023"
            style={{ fontSize: isMobile ? "16px" : "14px" }}
          />
        </Col>
      </Row>

      <br />
      <br />

      <h2 className="semi-bold" style={{ fontSize: "1.2rem" }}>
        Payment Details
      </h2>

      {userContext.credits > 0 && (
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#e6f7ff",
            border: "1px solid #91d5ff",
            borderRadius: "6px",
            marginBottom: "16px",
          }}
        >
          <p style={{ margin: 0, color: "#0050b3", fontWeight: 500 }}>
            You have ${userContext.credits} in credits available.
            {userContext.credits >= total + tipAmount &&
              " Your credits will cover this booking!"}
          </p>
        </div>
      )}

      <Row gutter={16}>
        {userContext.credits < total + tipAmount && (
          <Col xs={24} lg={12}>
            <label
              htmlFor="cardDetails"
              style={{ marginBottom: 5, display: "block" }}
            >
              Card Information
            </label>
            <CardElement
              options={{
                hidePostalCode: false,
                style: {
                  base: {
                    color: "#32325d",
                    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                    fontSmoothing: "antialiased",
                    fontSize: "16px",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                  },
                  invalid: {
                    color: "red",
                    iconColor: "red",
                  },
                },
              }}
            />
            <small style={{ color: "grey", marginTop: 6, display: "block" }}>
              Secured by Stripe. Your card information is encrypted and secure.
            </small>
          </Col>
        )}
        <Col lg={userContext.credits >= total + tipAmount ? 24 : 12} xs={24}>
          <label htmlFor="tipOptions">Leave a tip (Optional)</label>
          <StyledRadioGroup
            size="small"
            id="tipOptions"
            value={tipOption}
            onChange={(e) => handleTipOptionChange(e.target.value)}
            options={tipOptions}
          />
          {tipOption === "other" && (
            <InputNumber
              autoFocus
              className="input"
              placeholder="Enter tip amount"
              value={customTipAmount}
              onChange={handleCustomTipChange}
              formatter={(value: number | undefined) =>
                value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "") as any}
              size="large"
              min={0}
              max={100}
              step={5}
              style={{ width: "100%", marginTop: "8px" }}
            />
          )}
        </Col>
      </Row>

      <br />

      <Checkbox
        id="terms"
        checked={termsAccepted}
        onChange={(e) => setTermsAccepted(e.target.checked)}
        style={{ marginBottom: "16px" }}
      >
        I agree to the{" "}
        <a
          href="https://heyhomero.com/terms"
          target="_blank"
          rel="noopener noreferrer"
        >
          terms of service
        </a>
      </Checkbox>

      <br />
      <br />

      {isMobile ? (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fafafa",
            border: "1px solid #e2e2e2",
            borderRadius: "6px",
            marginBottom: "24px",
          }}
        >
          <h3
            style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: 600 }}
          >
            Order Summary
          </h3>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <span>Subtotal ({selectedTasks.length} tasks):</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <span>Tax (8.875%):</span>
            <span>${tax.toFixed(2)}</span>
          </div>
          {tipAmount > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <span>Tip:</span>
              <span>${tipAmount.toFixed(2)}</span>
            </div>
          )}
          <hr
            style={{
              margin: "12px 0",
              border: "none",
              borderTop: "1px solid #e2e2e2",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 600,
              fontSize: "18px",
            }}
          >
            <span>Total:</span>
            <span>${(total + tipAmount).toFixed(2)}</span>
          </div>
        </div>
      ) : null}

      {isMobile ? (
        <MobileNavbar
          loading={loading}
          nextAction={handleNext}
          prevAction={handlePrevious}
          prevText="Back"
          nextText="Complete Booking"
          type="task_booking"
          hidePrice={true}
          disabled={
            loading ||
            (!stripe && userContext.credits < total + tipAmount) ||
            !termsAccepted
          }
        />
      ) : (
        <ButtonGroup>
          <PreviousButton onClick={handlePrevious}>Previous</PreviousButton>
          <NextButton
            type="primary"
            onClick={handleNext}
            disabled={
              loading ||
              (!stripe && userContext.credits < total + tipAmount) ||
              !termsAccepted
            }
          >
            {loading ? "Processing..." : "Complete Booking"}
          </NextButton>
        </ButtonGroup>
      )}

      <CreditLoginModal
        visible={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onSuccess={handleCreditLoginSuccess}
        userEmail={userCreditsRef.current.email}
        userCredits={userCreditsRef.current.credits}
        authType={userCreditsRef.current.authType}
      />
    </Layout>
  );
}
